/**
 * The radar scope — a 2D canvas, drawn each frame.
 *
 * Canvas rather than DOM because this is the one HUD element that is genuinely a picture:
 * a rotating sweep, range rings and a few dozen blips. Doing it in the DOM would mean
 * dozens of transformed elements per frame for no gain in crispness.
 *
 * What it is allowed to show is decided by the simulation's detection model, not by this
 * file. A vessel that is only a radar return draws as a generic blip; the type icon
 * appears when the sim says the contact is identified. That is the whole point of the
 * detection ladder — the radar cannot lie about what you know.
 */

import type { Vessel, EntityId } from '../sim/types';
import { RADAR_RANGE, RADAR_RANGE_UPGRADED, SAFE_LINE_METRES } from '../sim/constants';
import { VESSEL_SPECS } from '../content/vessels';

/**
 * A hostile. Every hostile the radar draws is a live one.
 *
 * There used to be a second colour here for a hull that was going down, on the reasoning
 * that a wreck is still something on the water and the colour change is what tells the
 * gun to stop paying attention to it. Adam's call reverses that: the kill is the moment
 * the dot leaves. A wreck is indeed still something on the water, so it is shown out of
 * the window, where it is — the radar is for what still has to be dealt with.
 */
const RADAR_HOSTILE = '#e8862c';

/**
 * How much sea the scope draws to scale, metres.
 *
 * The instrument still *detects* to 10 km — this is only what it spends its face on.
 * Adam's call, and it settles a tension the display has had since it was built: the
 * radar's job is two different jobs. Inside a couple of kilometres it is a tactical
 * picture the player reads positions off; beyond that it is an early warning, where the
 * only questions are which way and roughly how soon. Trying to serve both on one ruler
 * meant serving neither — a linear 10 km scale buried the fight in the centre dot, and
 * the square root that replaced it made every distance a lie of a different kind, since
 * no part of the display was then true.
 */
const SCOPE_METRES = 3000;

/** Fraction of the face given to the true-scale band. */
const TRUE_SCALE_FRAC = 0.90;

/**
 * Where a contact beyond the true-scale band sits: on the rim, at its true bearing.
 *
 * Outside the band the radius stops meaning anything — a hull at 3 km and one at 9 km
 * are the same dot in the same place, and distance is carried entirely by opacity. The
 * gap between this and TRUE_SCALE_FRAC is what makes that legible: the rim is visibly a
 * different place from the outermost true range, not a continuation of it.
 */
const RIM_FRAC = 0.955;

/** Opacity of a contact at the very limit of radar range. Full brightness at SCOPE_METRES. */
const FAR_ALPHA = 0.2;

export interface RadarOptions {
  canvas: HTMLCanvasElement;
  longRange?: boolean;
  /**
   * Fired the instant the sweep first paints a contact. `hostile` is whether the vessel
   * answered IFF, which is the one thing a radar return genuinely knows about a hull it
   * has not yet resolved.
   */
  onAcquire?: (hostile: boolean) => void;
}

export class Radar {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  /**
   * Where the antenna is pointing, as a WORLD bearing.
   *
   * Deliberately not a screen angle. The display is head-up, so the picture turns when the
   * player turns — but the antenna does not, and if the sweep were stored in screen space
   * then looking left would drag the beam across every contact on that side and paint
   * them all. Keeping it in world space and rotating it only at draw time means a contact
   * is painted when the antenna actually reaches it.
   */
  private sweepAngle = 0;
  /** Contacts the sweep has passed over at least once, so they may be drawn. */
  private painted = new Set<EntityId>();
  private readonly onAcquire: ((hostile: boolean) => void) | null;
  longRange = false;

  constructor(opts: RadarOptions) {
    this.canvas = opts.canvas;
    this.longRange = opts.longRange ?? false;
    this.onAcquire = opts.onAcquire ?? null;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('radar: 2d context unavailable');
    this.ctx = ctx;
  }

  get range(): number {
    return this.longRange ? RADAR_RANGE_UPGRADED : RADAR_RANGE;
  }

  /**
   * Radial mapping: distance in metres → fraction of the scope's radius.
   *
   * Linear and true out to SCOPE_METRES, then pinned to the rim. Half the display used
   * to be spent on water nothing happens in; now the whole of it is the 3 km the player
   * fights in, at honest proportions — 800 m really is twice as far out as 400 m, which
   * no previous ruler could say.
   *
   * Everything distance-keyed goes through here — blips, the Safe Line, the range rings,
   * the uncertainty ellipse — so the picture stays self-consistent whatever the ruler is.
   */
  private toRadiusFrac(metres: number): number {
    const m = Math.max(0, metres);
    if (m >= SCOPE_METRES) return RIM_FRAC;
    return (m / SCOPE_METRES) * TRUE_SCALE_FRAC;
  }

  /**
   * How brightly a contact draws: distance, for everything out past the true-scale band.
   *
   * Once a contact is on the rim its radius has stopped carrying information, so opacity
   * takes the job over — full at the band's edge, fading to FAR_ALPHA at the limit of the
   * set. Two hulls on the same bearing at 3 km and 9 km land on the same spot and are
   * still telling the player different things, which is the whole point of the scheme.
   *
   * The ramp runs to `range`, so fitting the long-range set stretches it to 12 km rather
   * than making everything beyond 10 km equally dim.
   */
  private contactAlpha(metres: number): number {
    if (metres <= SCOPE_METRES) return 1;
    const span = Math.max(1, this.range - SCOPE_METRES);
    const t = Math.min(1, (metres - SCOPE_METRES) / span);
    return 1 + (FAR_ALPHA - 1) * t;
  }

  /** Resize the backing store to the element's CSS size at the device pixel ratio. */
  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  /**
   * Advance the paint state: mark every contact the beam crossed this frame.
   *
   * A contact does not exist on this display until the antenna has reached it. That is
   * both how the instrument works and where the acquisition tone belongs — the sound is
   * the sweep finding something, so it can only fire here.
   */
  private paintContacts(
    vessels: readonly Vessel[],
    from: number,
    to: number,
  ): void {
    // The sweep wrapped past zero this frame if it went backwards.
    const wrapped = to < from;
    const crossed = (bearing: number): boolean => {
      const b = (bearing + Math.PI * 2) % (Math.PI * 2);
      return wrapped ? (b > from || b <= to) : (b > from && b <= to);
    };

    const present = new Set<EntityId>();
    for (const v of vessels) {
      // Same test the contact loop draws by: a sinking hull is off the display, so it
      // must not hold a paint mark either — nor ring the acquisition tone for a kill.
      const detectable = v.detection !== 'undetected'
        && v.state !== 'dead' && v.state !== 'sinking'
        && Math.hypot(v.pos.x, v.pos.z) <= this.range;
      if (!detectable) continue;
      present.add(v.id);

      if (this.painted.has(v.id)) continue;
      if (!crossed(Math.atan2(v.pos.x, v.pos.z))) continue;

      this.painted.add(v.id);
      this.onAcquire?.(v.faction === 'hostile');
    }

    // Forget anything that has left the scope, so a contact that goes out of range and
    // comes back has to be found again rather than reappearing already painted.
    for (const id of this.painted) {
      if (!present.has(id)) this.painted.delete(id);
    }
  }

  /**
   * @param cameraYaw Where the player is looking, radians. The scope is drawn
   *   head-up — the direction the player faces is always toward the top, which is the
   *   only orientation that lets a blip be turned into a head movement without thought.
   */
  draw(
    vessels: readonly Vessel[],
    selectedId: EntityId | null,
    cameraYaw: number,
    dt: number,
  ): void {
    this.resize();
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) / 2 - 2;

    ctx.clearRect(0, 0, w, h);
    // Benchmark and promo capture deliberately hide the HUD. A hidden canvas can briefly
    // resize to 1×1; drawing a circle then would pass a negative radius to Canvas2D and
    // abort the entire render frame.
    if (radius <= 0) return;

    // Face.
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(6, 14, 12, 0.66)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(190, 210, 222, 0.34)';
    ctx.lineWidth = Math.max(1, radius * 0.012);
    ctx.stroke();

    // Range rings, evenly spaced now that the ruler is linear — which is the tell that it
    // is linear, and the reason they can be read as a ruler at all.
    ctx.strokeStyle = 'rgba(150, 190, 200, 0.16)';
    ctx.lineWidth = Math.max(1, radius * 0.007);
    for (const metres of [1000, 2000]) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius * this.toRadiusFrac(metres), 0, Math.PI * 2);
      ctx.stroke();
    }

    /*
     * The approach band: everything past the edge of true scale, drawn as a place rather
     * than a line.
     *
     * A stroke alone was not enough — against the face's own edge it read as a decorative
     * double border, and a contact sitting in the gap looked like it was simply near the
     * end of the ruler. Shading the annulus makes the rule visible without a word of
     * explanation: inside, a blip is where the hull is; in the band, only its bearing
     * survives and its brightness is the range. The eye learns that in one glance because
     * the band is a different surface, not a further gradation of the same one.
     */
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.arc(cx, cy, radius * TRUE_SCALE_FRAC, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(2, 8, 9, 0.55)';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cy, radius * TRUE_SCALE_FRAC, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(150, 190, 200, 0.34)';
    ctx.lineWidth = Math.max(1, radius * 0.009);
    ctx.stroke();

    /*
     * The Safe Line: the only distance on this display that is a rule rather than a
     * measurement, so it gets its own colour.
     *
     * It draws at 800 m rather than the old 200 m docking ring because that is where a
     * hull actually stops being the player's problem now — and because a shift that ends
     * the moment a tanker crosses needs the player to have been able to see the line she
     * was crossing. At the radar's 10 km scale the old ring was two per cent of the
     * display and told nobody anything.
     */
    const safeFrac = this.toRadiusFrac(SAFE_LINE_METRES);
    ctx.beginPath();
    ctx.arc(cx, cy, radius * safeFrac, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(88, 196, 138, 0.5)';
    ctx.setLineDash([radius * 0.05, radius * 0.04]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Cross hairs.
    ctx.strokeStyle = 'rgba(150, 190, 200, 0.14)';
    ctx.beginPath();
    ctx.moveTo(cx - radius, cy); ctx.lineTo(cx + radius, cy);
    ctx.moveTo(cx, cy - radius); ctx.lineTo(cx, cy + radius);
    ctx.stroke();

    // Sweep: a decaying wedge trailing the leading edge. Advanced in world bearing, then
    // rotated into the head-up picture at draw time.
    const previousSweep = this.sweepAngle;
    this.sweepAngle = (this.sweepAngle + dt * 1.15) % (Math.PI * 2);
    this.paintContacts(vessels, previousSweep, this.sweepAngle);
    const sweepScreen = this.sweepAngle - cameraYaw;

    const grad = ctx.createConicGradient
      ? ctx.createConicGradient(sweepScreen - Math.PI / 2, cx, cy)
      : null;
    if (grad) {
      grad.addColorStop(0, 'rgba(120, 230, 170, 0.30)');
      grad.addColorStop(0.14, 'rgba(120, 230, 170, 0.04)');
      grad.addColorStop(1, 'rgba(120, 230, 170, 0)');
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.sin(sweepScreen) * radius, cy - Math.cos(sweepScreen) * radius);
    ctx.strokeStyle = 'rgba(140, 245, 185, 0.5)';
    ctx.lineWidth = Math.max(1, radius * 0.012);
    ctx.stroke();

    // The player's own view cone — which slice of the sea is actually on screen.
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius * 0.30, -Math.PI / 2 - 0.52, -Math.PI / 2 + 0.52);
    ctx.closePath();
    ctx.fillStyle = 'rgba(214, 224, 230, 0.10)';
    ctx.fill();

    // Contacts.
    for (const v of vessels) {
      if (v.detection === 'undetected') continue;
      /*
       * A hull the player has killed leaves the display at once (Adam's direction).
       *
       * `sinking` runs for the whole dying animation — up to half a minute of burning on
       * the surface before a big hull even starts to settle — and the radar used to keep
       * drawing it in red with a strike through it for all of that. The intent was
       * "stop shooting this", but a scope full of contacts that no longer matter is the
       * opposite of what the display is for: it says the sector is busy when it is
       * already won. The kill is the moment the dot goes. What is left of the wreck is
       * shown where it actually is — out of the window, as a burning silhouette with
       * SUNK over it — until the sea takes it.
       */
      if (v.state === 'dead' || v.state === 'sinking') continue;
      // Nothing is drawn until the beam has been over it. This is the point of a sweep:
      // the display shows what the antenna has found, not what the game knows.
      if (!this.painted.has(v.id)) continue;

      const range = Math.hypot(v.pos.x, v.pos.z);
      if (range > this.range) continue;

      // Rotate into head-up space.
      const bearing = Math.atan2(v.pos.x, v.pos.z) - cameraYaw;
      const r = this.toRadiusFrac(range) * radius;
      const px = cx + Math.sin(bearing) * r;
      const py = cy - Math.cos(bearing) * r;
      const alpha = this.contactAlpha(range);

      /*
       * Allegiance comes from IFF, not from identification.
       *
       * These were gated on the contact being visually identified, which meant a hostile
       * stayed an anonymous grey dot for most of its approach — at night, often for all
       * of it. That also contradicted the acquisition tone, which announces on the very
       * first sweep whether the contact answered a transponder. A radar that pings
       * "hostile" and then draws "unknown" is telling the player two different things.
       *
       * What the radar still cannot tell you is *what* it is. That is what identification
       * is for, and it remains gated: the type label and the target panel stay blank until
       * the contact resolves.
       */
      const hostile = v.faction === 'hostile';
      const friendly = v.faction === 'friendly';

      const size = Math.max(2.4, radius * (v.kind === 'tanker' || v.kind === 'corvette' ? 0.05 : 0.034));

      /*
       * A contact held only on hydrophone, or lost entirely, is drawn as a region rather
       * than a point.
       *
       * This is the mini-sub encounter's core information problem made visible: the
       * player is not hunting a marker, they are hunting an area that widens while they
       * think about it. The ellipse grows from the boat's own top speed times how long
       * the track has been stale, so it is a genuine statement about where it could be
       * rather than a decorative fudge.
       */
      /*
       * Only inside the true-scale band. Out on the rim a region of sea has no width to
       * draw — every distance there maps to the same radius — so an ellipse would claim
       * a precision the display has given up. The hunt this exists for happens between
       * 600 m and 1.1 km, well inside the band.
       */
      if (v.trackAge > 1.5 && Math.hypot(v.lastKnownX, v.lastKnownZ) < SCOPE_METRES) {
        const spec = VESSEL_SPECS[v.kind];
        // The region's true size in metres, pushed through the compressed ruler at the
        // spot where it sits — the same span of sea reads wider close in than far out,
        // exactly like everything else on this display.
        const spreadMetres = spec.speed * v.trackAge;
        const lastRange = Math.hypot(v.lastKnownX, v.lastKnownZ);
        const uncertainty = radius * (
          this.toRadiusFrac(lastRange + spreadMetres)
          - this.toRadiusFrac(Math.max(0, lastRange - spreadMetres))
        ) / 2;
        const lastFrac = this.toRadiusFrac(lastRange);
        const lx = cx + Math.sin(Math.atan2(v.lastKnownX, v.lastKnownZ) - cameraYaw)
          * lastFrac * radius;
        const ly = cy - Math.cos(Math.atan2(v.lastKnownX, v.lastKnownZ) - cameraYaw)
          * lastFrac * radius;

        ctx.beginPath();
        ctx.ellipse(lx, ly, uncertainty, uncertainty * 0.82, 0, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(120, 190, 220, 0.34)';
        ctx.setLineDash([radius * 0.03, radius * 0.03]);
        ctx.lineWidth = Math.max(1, radius * 0.008);
        ctx.stroke();
        ctx.setLineDash([]);
        // Past this the region is bigger than the useful scope and the honest answer is
        // "somewhere out there", so the blip itself is withheld.
        if (uncertainty > radius * 0.5) continue;
      }

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      if (hostile) {
        // Hostiles are angular, friendlies are round: readable without colour, which
        // matters both for colour-blind players and in a bright cabin. Every hostile the
        // radar draws is now a live one — a kill leaves the display immediately — so the
        // colour no longer has to carry "going down" as a second fact.
        ctx.moveTo(px, py - size);
        ctx.lineTo(px + size, py);
        ctx.lineTo(px, py + size);
        ctx.lineTo(px - size, py);
        ctx.closePath();
        ctx.fillStyle = RADAR_HOSTILE;
        ctx.fill();
      } else if (friendly) {
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fillStyle = '#58c48a';
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (v.id === selectedId) {
        // The selection ring keeps a floor: dimming is how the display says "far away",
        // and losing track of the contact the player deliberately picked is not a thing
        // distance should be able to do.
        ctx.globalAlpha = Math.max(alpha, 0.55);
        ctx.beginPath();
        ctx.arc(px, py, size * 2.1, 0, Math.PI * 2);
        ctx.strokeStyle = '#e0a63c';
        ctx.lineWidth = Math.max(1, radius * 0.014);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // The rig itself.
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(2, radius * 0.028), 0, Math.PI * 2);
    ctx.fillStyle = '#e0e6ea';
    ctx.fill();
  }
}
